/**
 * moduloContratado.js — ¿la empresa CONTRATÓ este módulo? (entitlement comercial)
 *
 * Espejo textual de `functions/lib/moduloContratado.js` (server) — misma
 * semántica, y `moduloContratado.test.js` carga las DOS puntas y corre cada
 * caso contra ambas. Vive en un archivo hoja (sin `firebase`) para que
 * cualquier test lo cargue sin mockear la SDK. `modulosDisponibles` lo escribe
 * el servidor (callable `escribirModulosEmpresas`); en el navegador la puerta es
 * `planes.js`, que avisa antes y llama al callable.
 *
 * Dos defaults conviven a propósito en el front, igual que en el server:
 *   · VISIBILIDAD (sidebar, vitrina): `useModulosVisibles`, `incluidoEnPlan`
 *     de `cuadroGeneral.js` — sin listas declaradas, la empresa ve todo.
 *   · ENTITLEMENT (qué puede CONECTAR, y qué tableros de canal ve
 *     encendidos): esto — sin lista declarada NO contrató nada. Es el
 *     criterio que ya tenían la card de plataformas (`EmpresaIntegraciones`)
 *     y el gate de dashboards, cada uno con su copia; acá queda UNA. Que los
 *     tableros de un canal vayan por el plan y no por visibilidad, aunque se
 *     muestren, lo decide el ADR 2026-08-29 de las palancas de una
 *     plataforma, §3.
 *
 * Sólo mira `modulosDisponibles` (lo que el PLAN le dio, congelado para el
 * tenant en rules), no `modulosActivados` (toggle que la empresa edita).
 *
 * @param {object|null|undefined} empresa
 * @param {string|null|undefined} moduloId  código de módulo del catálogo
 * @returns {boolean}
 */
export function moduloContratadoPor(empresa, moduloId) {
  if (!empresa || typeof empresa !== 'object') return false
  if (typeof moduloId !== 'string' || !moduloId) return false
  const disponibles = empresa.modulosDisponibles
  if (!Array.isArray(disponibles)) return false
  return disponibles.includes(moduloId)
}

/**
 * PURO. ¿Se MUESTRA el módulo para la empresa? — espejo de
 * `functions/lib/moduloContratado.js` (`moduloHabilitadoEn`), con la paridad en
 * `moduloContratado.test.js`. Es VISIBILIDAD: sin listas declaradas, la empresa
 * ve todo (el default permisivo de `useModulosVisibles`). Lo usan las
 * superficies de staff que espejan lo que ve el cliente (las solapas de
 * Reportes de la ficha).
 *
 * @param {object|null|undefined} empresa
 * @param {string|null|undefined} modulo
 * @returns {boolean}
 */
export function moduloHabilitadoEn(empresa, modulo) {
  if (!empresa) return false
  const disponibles = empresa.modulosDisponibles
  const activados = empresa.modulosActivados
  const enDisponibles = !disponibles || disponibles.length === 0 || disponibles.includes(modulo)
  const enActivados = !activados || activados.length === 0 || activados.includes(modulo)
  return enDisponibles && enActivados
}

// ─── ¿Puede PRODUCIR trabajo de este módulo? — espejo de functions/lib/moduloContratado.js
// La tabla de los tres predicados (visibilidad / contratado / operable) y el
// porqué de cada caso viven en el server y en el ADR
// `2026-10-08-tareas-y-agentes-operan-solo-donde-el-plan-los-incluye-y-la-.md`.
// `moduloContratado.test.js` corre la misma tabla contra las dos puntas.

export const CAUSAS_OPERABLE = Object.freeze({
  OPERABLE: 'operable',
  PLAN_SIN_SINCRONIZAR: 'plan-sin-sincronizar',
  FUERA_DEL_PLAN: 'fuera-del-plan',
  APAGADO_POR_LA_CUENTA: 'apagado-por-la-cuenta',
  ILEGIBLE: 'ilegible',
  EMPRESA_INEXISTENTE: 'empresa-inexistente',
})

const _estado = (operable, causa) => ({ operable, causa })

/**
 * PURO. ¿La empresa puede producir trabajo de `modulo`? Contratado Y no
 * apagado por la cuenta (`modulosActivados` sólo apaga). Fail-closed.
 *
 * @param {object|null|undefined} empresa
 * @param {string|null|undefined} modulo
 * @returns {{ operable: boolean, causa: string }}
 */
export function estadoOperable(empresa, modulo) {
  if (!empresa || typeof empresa !== 'object') return _estado(false, CAUSAS_OPERABLE.ILEGIBLE)
  if (typeof modulo !== 'string' || !modulo) return _estado(false, CAUSAS_OPERABLE.ILEGIBLE)
  const disponibles = empresa.modulosDisponibles
  if (disponibles === undefined || disponibles === null) return _estado(false, CAUSAS_OPERABLE.PLAN_SIN_SINCRONIZAR)
  if (!Array.isArray(disponibles)) return _estado(false, CAUSAS_OPERABLE.ILEGIBLE)
  if (disponibles.length === 0) return _estado(false, CAUSAS_OPERABLE.PLAN_SIN_SINCRONIZAR)
  if (!disponibles.includes(modulo)) return _estado(false, CAUSAS_OPERABLE.FUERA_DEL_PLAN)
  const activados = empresa.modulosActivados
  if (activados === undefined || activados === null) return _estado(true, CAUSAS_OPERABLE.OPERABLE)
  if (!Array.isArray(activados)) return _estado(false, CAUSAS_OPERABLE.ILEGIBLE)
  if (activados.length === 0 || activados.includes(modulo)) return _estado(true, CAUSAS_OPERABLE.OPERABLE)
  return _estado(false, CAUSAS_OPERABLE.APAGADO_POR_LA_CUENTA)
}

/** PURO. Atajo booleano de `estadoOperable`. */
export function moduloOperablePor(empresa, modulo) {
  return estadoOperable(empresa, modulo).operable
}

// ─── «Ninguno prendido» — el escritor único de `modulosActivados` ──────────────
//
// `modulosActivados` vacío o ausente significa «todos los disponibles prendidos»
// (la forma del alta, `functions/empresas/formaTenant.js`, y el default del
// schema). Esa convención no puede representar «ninguno»: apagar el último
// módulo escribía `[]` y la cuenta volvía a verlo todo prendido. «Ninguno» se
// escribe como `[MODULOS_NINGUNO]`: no está vacío y no incluye ningún módulo
// real, así que todos los lectores (`estadoOperable`, `moduloHabilitadoEn`,
// `useModulosVisibles`, `ModulosToggle`) ya lo leen como «todo apagado» sin
// conocer el centinela. Report uzRaiHPlGXh4qe2zYdxc.
//
// Ninguna escritura de `modulosActivados` calcula la lista por su cuenta: pasa
// por `siguientesActivados` / `activadosDentroDelPlan`
// (`modulosActivados.escritorUnico.guard.test.js` lo hace cumplir).

export const MODULOS_NINGUNO = '__ninguno__'

const veTodo = (lista) => !Array.isArray(lista) || lista.length === 0

/** PURO. Los módulos reales de una lista de activados, sin el centinela. */
export function activadosReales(activados) {
  return Array.isArray(activados) ? activados.filter((id) => id !== MODULOS_NINGUNO) : []
}

// Una lista sin módulos reales se escribe como «ninguno», nunca como `[]`.
export const comoLista = (ids) => (ids.length > 0 ? ids : [MODULOS_NINGUNO])

/**
 * PURO. La lista que hay que escribir al prender o apagar UN módulo.
 * `activados` vacío o ausente parte de todos los `disponibles`.
 *
 * @param {{ activados: string[]|null|undefined, disponibles: string[], moduloId: string, prender: boolean }} p
 * @returns {string[]} nunca `[]`: apagar el último devuelve `[MODULOS_NINGUNO]`
 */
export function siguientesActivados({ activados, disponibles, moduloId, prender }) {
  const base = veTodo(activados) ? [...disponibles] : activadosReales(activados)
  const sin = base.filter((id) => id !== moduloId)
  return comoLista(prender ? [...sin, moduloId] : sin)
}

/**
 * PURO. La lista de activados que queda al reescribir los `modulosDisponibles`
 * de la empresa: los que ya no están en el plan se limpian. Una cuenta que
 * apagó todo sigue con todo apagado (el filtro no la vuelve a «todo prendido»).
 * Con el plan nuevo vacío o ausente no hay nada que limpiar (el escritor de
 * `modulosDisponibles`, `planes.js`, lo usa así cuando escribe «ninguno»).
 *
 * @param {{ activados: string[]|null|undefined, modulosDelPlan: string[]|null|undefined }} p
 * @returns {string[]}
 */
export function activadosDentroDelPlan({ activados, modulosDelPlan }) {
  if (veTodo(activados)) return []
  if (veTodo(modulosDelPlan)) return [...activados]
  return comoLista(activadosReales(activados).filter((id) => modulosDelPlan.includes(id)))
}

/**
 * PURO. La lista de `modulosDisponibles` que se escribe: sin el centinela y,
 * si no queda ningún módulo, `[MODULOS_NINGUNO]` — nunca `[]`, que la empresa
 * leería como «ve todo». Una lista que no es array es un error del llamador.
 * Espejo de `functions/lib/moduloContratado.js`, que usa el escritor del
 * servidor (`escribirModulosEmpresas`), con la paridad en
 * `moduloContratado.test.js`.
 */
export function listaDisponiblesAEscribir(lista) {
  if (!Array.isArray(lista)) throw new Error('listaDisponiblesAEscribir: la lista de módulos tiene que ser un array')
  return comoLista(activadosReales(lista))
}

/** PURO. ¿Pasar de `antes` a `despues` le puede sacar algún módulo a la empresa? */
export function quitaModulos(antes, despues) {
  const nueva = activadosReales(despues)
  if (veTodo(antes)) return true
  return activadosReales(antes).some((m) => !nueva.includes(m))
}
